// TileBackdrop.js
//
// Satellite imagery as the editing backdrop, drawn in PLANNING-FRAME coordinates.
//
// Why not a live slippy map: the editing core's pan/zoom is owned by
// react-zoom-pan-pinch, and Leaflet/MapLibre insist on owning pan/zoom
// themselves. Two pan/zoom systems fighting is how the two planning views would
// drift apart. So the tiles are plain <img>s positioned inside the frame box:
// ONE pan/zoom, and every existing drawing tool keeps working untouched.
//
// This layer is VIEWPORT-AWARE. It renders only the tiles that are actually on
// screen, at a zoom matched to the current display scale, and re-picks both as
// you pan and zoom. That is what makes imagery sharpen when you zoom in, and
// what keeps a multi-kilometre canvas affordable -- an earlier version chose one
// zoom for the whole frame at open time, so imagery never refined and a large
// canvas blew the tile budget.
//
// Tiles are Web Mercator XYZ; the frame is UTM-linear. Over a viewport-sized
// area the difference is far below a pixel, so each tile is placed by converting
// its lon/lat corners into frame coordinates.
//
// Esri World Imagery: keyless, attribution REQUIRED (rendered by the caller).
//
//   [ADR: docs/20260826_satellite_kml_planning_frame.html]

import React, { useMemo, useRef, useState, useLayoutEffect, useEffect, useCallback } from 'react';

const ESRI = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile';
export const ESRI_ATTRIBUTION =
  'Imagery © Esri — Esri, Maxar, Earthstar Geographics, and the GIS User Community';

// Esri World Imagery is native to z19 in most of the US; past that the server
// 404s rather than upscaling, so cap requests and let the browser scale.
export const MAX_NATIVE_ZOOM = 19;
export const MIN_ZOOM = 8;
export const TILE_BUDGET = 400;

const EARTH_C = 156543.03392; // metres per pixel at z0 on the equator

export const groundPerTilePx = (lat, z) => (EARTH_C * Math.cos((lat * Math.PI) / 180)) / 2 ** z;
export const groundPerTile = (lat, z) => groundPerTilePx(lat, z) * 256;

const lonToTileX = (lon, z) => ((lon + 180) / 360) * 2 ** z;
const latToTileY = (lat, z) => {
  const s = Math.sin((lat * Math.PI) / 180);
  return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * 2 ** z;
};
const tileXToLon = (x, z) => (x / 2 ** z) * 360 - 180;
const tileYToLat = (y, z) => {
  const n = Math.PI - (2 * Math.PI * y) / 2 ** z;
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
};

// Finest zoom whose grid over `extentW x extentH` fits the budget. Used as a
// safety clamp so a degenerate viewport measurement can never fire thousands of
// requests at the tile server.
export function chooseZoom(extentW, extentH, lat, budget = TILE_BUDGET) {
  for (let z = MAX_NATIVE_ZOOM; z >= MIN_ZOOM; z -= 1) {
    const g = groundPerTile(lat, z);
    const n = (Math.ceil(extentW / g) + 1) * (Math.ceil(extentH / g) + 1);
    if (n <= budget) return { zoom: z, tiles: n };
  }
  return { zoom: MIN_ZOOM, tiles: budget };
}

// Zoom whose tile pixels are about the size of a screen pixel -- i.e. imagery
// at the detail the display can actually show.
export function zoomForGroundResolution(metresPerScreenPx, lat) {
  if (!isFinite(metresPerScreenPx) || metresPerScreenPx <= 0) return MAX_NATIVE_ZOOM;
  const z = Math.log2((EARTH_C * Math.cos((lat * Math.PI) / 180)) / metresPerScreenPx);
  return Math.max(MIN_ZOOM, Math.min(MAX_NATIVE_ZOOM, Math.round(z)));
}

export default function TileBackdrop({ frame, currentScale = 1, position = null, opacity = 1 }) {
  const rootRef = useRef(null);
  const [view, setView] = useState(null);

  // Measure where the frame box currently sits on screen and intersect it with
  // the window. This reads the DOM rather than reconstructing the pan/zoom
  // matrix, so it stays correct no matter how react-zoom-pan-pinch composes its
  // transform. currentScale/position are dependencies purely to re-trigger it.
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el || !frame) return;
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.max(0, -r.left);
    const top = Math.max(0, -r.top);
    const right = Math.min(r.width, vw - r.left);
    const bottom = Math.min(r.height, vh - r.top);
    if (right <= left || bottom <= top) { setView(null); return; }

    const toFrameX = (px) => (px / r.width) * frame.width;
    const toFrameY = (py) => (py / r.height) * frame.height;
    const next = {
      x0: toFrameX(left), x1: toFrameX(right),
      y0: toFrameY(top), y1: toFrameY(bottom),
      // Ground metres per on-screen CSS pixel, which sets the useful tile zoom.
      mPerScreenPx: (frame.width * frame.mPerUnit) / r.width,
    };
    // Publish only on a MATERIAL change. react-zoom-pan-pinch fires
    // onPanningStop for every click-release -- even a click that never moved --
    // which calls setPosition with a fresh object. Without this guard each
    // click produced a new `view` object, invalidating the tile memo and
    // remounting every tile <img>; the old tiles vanished instantly while the
    // replacements were still fetching, so the backdrop flashed black. The
    // threshold is a fraction of the visible span, well below one tile.
    setView((prev) => {
      if (prev) {
        const spanX = Math.max(1, prev.x1 - prev.x0);
        const spanY = Math.max(1, prev.y1 - prev.y0);
        const moved = Math.abs(next.x0 - prev.x0) / spanX > 0.02
                   || Math.abs(next.y0 - prev.y0) / spanY > 0.02
                   || Math.abs(next.x1 - prev.x1) / spanX > 0.02
                   || Math.abs(next.y1 - prev.y1) / spanY > 0.02
                   || Math.abs(Math.log2(next.mPerScreenPx / prev.mPerScreenPx)) > 0.15;
        if (!moved) return prev;   // identical reference -> memo holds -> no churn
      }
      return next;
    });
  }, [frame, currentScale, position]);

  const tiles = useMemo(() => {
    if (!frame || !frame.pixelToLonLat || !view) return { list: [], z: null };

    const lat = frame.centerLat ?? 0;
    // Match imagery detail to the display, then clamp so the visible span can
    // never exceed the budget.
    const wanted = zoomForGroundResolution(view.mPerScreenPx, lat);
    const spanW = (view.x1 - view.x0) * frame.mPerUnit;
    const spanH = (view.y1 - view.y0) * frame.mPerUnit;
    const z = Math.min(wanted, chooseZoom(spanW, spanH, lat).zoom);

    // Visible frame rect -> lon/lat -> tile index range, padded by one tile so
    // panning does not reveal bare edges before the next measurement lands.
    const nw = frame.pixelToLonLat(view.x0, view.y0);
    const se = frame.pixelToLonLat(view.x1, view.y1);
    const x0 = Math.floor(lonToTileX(Math.min(nw.lon, se.lon), z)) - 1;
    const x1 = Math.ceil(lonToTileX(Math.max(nw.lon, se.lon), z)) + 1;
    const y0 = Math.floor(latToTileY(Math.max(nw.lat, se.lat), z)) - 1;
    const y1 = Math.ceil(latToTileY(Math.min(nw.lat, se.lat), z)) + 1;

    const max = 2 ** z;
    const list = [];
    for (let ty = y0; ty < y1 && list.length < TILE_BUDGET; ty += 1) {
      for (let tx = x0; tx < x1 && list.length < TILE_BUDGET; tx += 1) {
        if (tx < 0 || ty < 0 || tx >= max || ty >= max) continue;
        const [px0, py0] = frame.lonLatToPixel(tileXToLon(tx, z), tileYToLat(ty, z));
        const [px1, py1] = frame.lonLatToPixel(tileXToLon(tx + 1, z), tileYToLat(ty + 1, z));
        list.push({
          key: `${z}/${tx}/${ty}`,
          url: `${ESRI}/${z}/${ty}/${tx}`,
          left: (px0 / frame.width) * 100,
          top: (py0 / frame.height) * 100,
          // The frame is true-north aligned, so adjacent tiles share corners and
          // abut EXACTLY -- no percentage fudge needed (the old +0.04% was half
          // a metre of overlap on a frame this size). One extra unit closes the
          // sub-pixel anti-aliasing seam without shifting anything visibly.
          width: ((px1 - px0 + 1) / frame.width) * 100,
          height: ((py1 - py0 + 1) / frame.height) * 100,
        });
      }
    }
    return { list, z };
  }, [frame, view]);

  // Crossfade-free handover between tile sets. When the zoom level or tile range
  // genuinely changes, React would unmount every old <img> immediately while the
  // replacements are still fetching, leaving the bare background showing through
  // as black rectangles for the duration of the request. Instead the outgoing set
  // is RETAINED underneath until the incoming one has loaded -- what a slippy map
  // does. Purely visual: it never changes which tiles are considered current.
  const [retained, setRetained] = useState([]);
  const currentRef = useRef({ sig: '', list: [] });
  const loadedRef = useRef(new Set());

  const sig = tiles.list.map((t) => t.key).join('|');
  useEffect(() => {
    if (sig === currentRef.current.sig) return;
    if (currentRef.current.list.length) setRetained(currentRef.current.list);
    loadedRef.current = new Set();
    currentRef.current = { sig, list: tiles.list };
    // Safety net: never strand the old layer if a tile errors or stalls.
    const t = setTimeout(() => setRetained([]), 4000);
    return () => clearTimeout(t);
  }, [sig, tiles.list]);

  const handleLoaded = useCallback((key) => {
    loadedRef.current.add(key);
    if (loadedRef.current.size >= currentRef.current.list.length) setRetained([]);
  }, []);

  if (!frame) return null;

  const renderTile = (t, isRetained) => (
    <img
      key={(isRetained ? 'r:' : '') + t.key}
      src={t.url}
      alt=""
      draggable={false}
      style={{
        position: 'absolute',
        left: `${t.left}%`, top: `${t.top}%`,
        width: `${t.width}%`, height: `${t.height}%`,
        opacity,
        zIndex: isRetained ? 0 : 1,
        userSelect: 'none',
        pointerEvents: 'none',
      }}
      onLoad={isRetained ? undefined : () => handleLoaded(t.key)}
      onError={(e) => {
        e.currentTarget.style.visibility = 'hidden';
        if (!isRetained) handleLoaded(t.key);   // errors must not strand the old layer
      }}
    />
  );

  return (
    <div
      ref={rootRef}
      style={{
        position: 'absolute', inset: 0, overflow: 'hidden', background: '#1a1a1a',
        // `isolation: isolate` creates a stacking context so the retained/current
        // z-indices below stay INSIDE this layer. Without it they compete with
        // siblings in the parent stacking context, and the tiles painted over the
        // KML reference geometry -- the overlay was rendering correctly and was
        // simply hidden behind the imagery.
        isolation: 'isolate',
        zIndex: 0,
        // A backdrop must never take pointer input: the pan/zoom and drawing handlers
        // live on an ancestor, and a div that swallows wheel/mouse events silently
        // disables zooming over the imagery.
        pointerEvents: 'none',
      }}
    >
      {retained.map((t) => renderTile(t, true))}
      {tiles.list.map((t) => renderTile(t, false))}
    </div>
  );
}
